import { toastServerError } from '../global/notifications'
import { capturePosthogEvent } from './posthog'
import { SESSION_EXPIRED_PARAM, withoutSessionFlag } from './sessionFlag'

export { SESSION_EXPIRED_PARAM, withoutSessionFlag }

// SPEC-095: why the session ended. 'expired' covers expired, undecodable and
// pre-migration tokens; 'other_device' is the single-session rule.
export const SESSION_EXPIRED_REASONS = ['expired', 'other_device']
export const SESSION_EXPIRED_MESSAGES = {
  expired: 'Your session expired. Please log in again.',
  other_device:
    'You were logged out because your account was signed in on another device. Please log in again.'
}

// Recovery after the session is torn down mid-page: the page's other requests
// already failed with the dead token, so an SPA navigation would leave their
// reducers holding errors — only a full re-bootstrap recovers them. Persisted
// state must be flushed before the reload, or the cleared auth slice never
// reaches localStorage and the reload resurrects the dead token in a loop.
// The store is imported dynamically because store.js imports the middlewares
// that call this.
// SPEC-095: the reload stays on the page the visitor was reading; the app
// decides on boot whether that page needs a login (see sessionExpiredOnBoot).
export const reloadAsLoggedOut = async (reason = 'expired') => {
  // Read the page now, before anything awaits: once the auth slice clears,
  // a guard navigates the page to /login, and the reload must land on the
  // page the visitor was on so the app can decide what it needs.
  const url = new URL(window.location.href)
  url.searchParams.set(SESSION_EXPIRED_PARAM, reason)
  try {
    const { persistor } = await import('../redux/store')
    if (persistor) await persistor.flush()
  } catch (_) {
    // The flush is best-effort: redux-persist also writes on every state
    // change, so the cleared auth slice has likely already landed. A stuck
    // broken page is worse than a rare extra reload, so redirect regardless.
  }
  // replace, not assign: the reload must not add a history entry (Back
  // would land on the same page, or on the guard's /login twice)
  window.location.replace(url.pathname + url.search + url.hash)
}

// Reads the flag reloadAsLoggedOut left in the URL once the app boots, given
// the page it booted on (route pattern + whether it needs a login, from
// routeAuthInfo). On a page that needs a login the guard sends the visitor
// to /login with a return-to, and the plain message explains why; on a
// public page the logout is silent and the visitor keeps reading.
// Returns the reason, or null when the flag is absent.
export const sessionExpiredOnBoot = (search, { route, authRequired }) => {
  const flag = new URLSearchParams(search).get(SESSION_EXPIRED_PARAM)
  if (!flag) return null
  // '1' is the pre-SPEC-095 flag (an in-flight reload during the release)
  const reason = SESSION_EXPIRED_REASONS.includes(flag) ? flag : 'expired'
  if (authRequired) toastServerError(SESSION_EXPIRED_MESSAGES[reason])
  capturePosthogEvent('session_expired_logout', {
    route,
    reason,
    redirected: Boolean(authRequired)
  })
  return reason
}
